/**
 * Admin — Categories + Services CRUD.
 *
 * Layout:
 *   • Categories bar at top: chips + inline "New category" + rename/delete.
 *   • Services table grouped by category with column headers.
 *   • Each category block has its own "+ Add service" row.
 *   • Editing a row highlights the Save button — bright orange when dirty.
 */
import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, Trash2, X, Check, Pencil, GripVertical } from "lucide-react";
import { CategoryIcon, CATEGORY_ICON_OPTIONS } from "@/lib/categoryIcons";

const EMPTY_SVC = { name: "", category: "", duration_min: 45, price: 0, description: "", active: true, included_in: [] };
const MEMBERSHIP_KEYS = [
  { id: "essential",   label: "E", full: "Essential",   color: "#94A3B8" },
  { id: "progress",    label: "P", full: "Progress",    color: "#3B82F6" },
  { id: "performance", label: "Pf", full: "Performance", color: "#f47a20" },
];

export default function AdminServices() {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [dirty, setDirty] = useState({});
  const [addingTo, setAddingTo] = useState(null);
  const [newSvc, setNewSvc] = useState({ ...EMPTY_SVC });
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCat, setNewCat] = useState({ slug: "", name: "", description: "" });
  const [renamingSlug, setRenamingSlug] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [dragSlug, setDragSlug] = useState(null);
  // Row-level drag state — {rowId, slug} — separate from category-chip drag so
  // hovering rows during a chip drag doesn't accidentally reorder services.
  const [dragRow, setDragRow] = useState(null);

  const loadAll = async () => {
    const [cs, ss] = await Promise.all([
      api.get("/admin/categories"),
      api.get("/admin/services"),
    ]);
    setCategories(cs.data);
    setServices(ss.data);
    setDirty({});
  };
  useEffect(() => { loadAll(); }, []);

  const grouped = useMemo(() => {
    const map = {};
    for (const c of categories) map[c.slug] = [];
    for (const s of services) {
      const bucket = map[s.category] ?? (map.__other = map.__other || []);
      bucket.push(s);
    }
    // Sort by (display_order asc, name asc) so drag-and-drop reordering wins
    // over alphabetical. Services created before the reorder feature have
    // display_order=undefined and fall to the back.
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => {
        const ao = a.display_order ?? 9999;
        const bo = b.display_order ?? 9999;
        if (ao !== bo) return ao - bo;
        return a.name.localeCompare(b.name);
      });
    }
    return map;
  }, [categories, services]);

  const patchRow = (id, patch) => {
    setServices((list) => list.map((s) => s.id === id ? { ...s, ...patch } : s));
    setDirty((d) => ({ ...d, [id]: true }));
  };

  const saveRow = async (svc) => {
    const payload = {
      name: svc.name.trim(),
      category: svc.category,
      duration_min: parseInt(svc.duration_min, 10) || 0,
      price: parseFloat(svc.price) || 0,
      description: (svc.description || "").trim(),
      active: !!svc.active,
      included_in: svc.included_in || [],
    };
    try {
      await api.patch(`/admin/services/${svc.id}`, payload);
      toast.success(`Saved · ${svc.name}`);
      setDirty((d) => { const nd = { ...d }; delete nd[svc.id]; return nd; });
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || "unknown"}`);
    }
  };

  const toggleMembership = (svc, memId) => {
    const cur = svc.included_in || [];
    const next = cur.includes(memId) ? cur.filter((x) => x !== memId) : [...cur, memId];
    patchRow(svc.id, { included_in: next });
  };

  const persistCategoryOrder = async (slugs) => {
    try {
      await api.post("/admin/categories/reorder", { slugs });
      toast.success("Order saved");
    } catch { toast.error("Could not save order"); }
  };

  const handleDragOver = (targetSlug) => {
    if (!dragSlug || dragSlug === targetSlug) return;
    setCategories((cats) => {
      const from = cats.findIndex((c) => c.slug === dragSlug);
      const to = cats.findIndex((c) => c.slug === targetSlug);
      if (from < 0 || to < 0) return cats;
      const next = [...cats];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };
  const handleDragEnd = () => {
    if (!dragSlug) return;
    setDragSlug(null);
    persistCategoryOrder(categories.map((c) => c.slug));
  };

  // ----- Service row drag-and-drop within one category -----
  const persistRowOrder = async (slug, ids) => {
    try {
      await api.post("/admin/services/reorder", { category: slug, service_ids: ids });
      toast.success("Order saved");
    } catch { toast.error("Could not save order"); }
  };
  const handleRowDragOver = (targetId, slug) => {
    if (!dragRow || dragRow.slug !== slug || dragRow.rowId === targetId) return;
    setServices((list) => {
      // Reorder inside the sorted view of THIS category, then map back into
      // the flat services array by rewriting display_order for every row in
      // the category (0..N).
      const sameCat = list
        .filter((s) => s.category === slug)
        .sort((a, b) => (a.display_order ?? 9999) - (b.display_order ?? 9999) || a.name.localeCompare(b.name));
      const from = sameCat.findIndex((s) => s.id === dragRow.rowId);
      const to = sameCat.findIndex((s) => s.id === targetId);
      if (from < 0 || to < 0) return list;
      const [moved] = sameCat.splice(from, 1);
      sameCat.splice(to, 0, moved);
      const orderById = new Map(sameCat.map((s, i) => [s.id, i]));
      return list.map((s) =>
        s.category === slug ? { ...s, display_order: orderById.get(s.id) ?? s.display_order } : s
      );
    });
  };
  const handleRowDragEnd = () => {
    if (!dragRow) return;
    const slug = dragRow.slug;
    setDragRow(null);
    // Read the just-updated state via a functional setState trick — we already
    // wrote display_order in the drag-over handler; here we just persist the
    // final order for this category.
    setServices((list) => {
      const ids = list
        .filter((s) => s.category === slug)
        .sort((a, b) => (a.display_order ?? 9999) - (b.display_order ?? 9999))
        .map((s) => s.id);
      persistRowOrder(slug, ids);
      return list;
    });
  };

  const deleteRow = async (svc) => {
    if (!window.confirm(`Delete "${svc.name}"?`)) return;
    try {
      await api.delete(`/admin/services/${svc.id}`);
      toast.success("Deleted");
      loadAll();
    } catch { toast.error("Delete failed"); }
  };

  const addService = async (categorySlug) => {
    const payload = {
      ...newSvc,
      category: categorySlug,
      duration_min: parseInt(newSvc.duration_min, 10) || 0,
      price: parseFloat(newSvc.price) || 0,
    };
    if (!payload.name.trim()) { toast.error("Name is required"); return; }
    try {
      await api.post("/admin/services", payload);
      toast.success("Service added");
      setAddingTo(null);
      setNewSvc({ ...EMPTY_SVC });
      loadAll();
    } catch (e) {
      toast.error(`Failed: ${e?.response?.data?.detail || "unknown"}`);
    }
  };

  const createCategory = async () => {
    if (!newCat.slug || !newCat.name) { toast.error("Slug and name are required"); return; }
    try {
      await api.post("/admin/categories", { ...newCat, order: (categories.at(-1)?.order || 0) + 10 });
      toast.success("Category created");
      setShowNewCat(false);
      setNewCat({ slug: "", name: "", description: "" });
      loadAll();
    } catch (e) {
      toast.error(`Failed: ${e?.response?.data?.detail || "unknown"}`);
    }
  };

  const renameCategory = async (slug) => {
    if (!renameValue.trim()) return;
    try {
      await api.patch(`/admin/categories/${slug}`, { name: renameValue.trim() });
      toast.success("Category renamed");
      setRenamingSlug(null);
      loadAll();
    } catch { toast.error("Rename failed"); }
  };

  const setCategoryIcon = async (cat, icon) => {
    // Optimistic
    setCategories((cs) => cs.map((c) => c.slug === cat.slug ? { ...c, icon } : c));
    try {
      await api.patch(`/admin/categories/${cat.slug}`, { name: cat.name, icon });
      toast.success("Icon updated");
    } catch { toast.error("Could not update icon"); loadAll(); }
  };

  const deleteCategory = async (slug) => {
    if (!window.confirm(`Delete category "${slug}"? This will fail if any services still use it.`)) return;
    try {
      await api.delete(`/admin/categories/${slug}`);
      toast.success("Category deleted");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot delete");
    }
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-end mb-6">
        <div>
          <div className="fl-label text-[#f47a20]">Catalogue</div>
          <h1 className="fl-heading text-4xl mt-2" data-testid="admin-services-heading">
            {services.length} services · {categories.length} categories
          </h1>
          <div className="text-xs text-zinc-500 mt-1">
            Changes here update the public <b>/prices</b> page instantly.
          </div>
        </div>
        <button
          onClick={() => setShowNewCat(!showNewCat)}
          className="fl-btn"
          data-testid="admin-new-category-btn"
        >
          <Plus size={14} /> New category
        </button>
      </div>

      {showNewCat && (
        <div className="fl-card p-4 mb-6 grid md:grid-cols-4 gap-3 border-[#f47a20]/40" data-testid="admin-new-category-form">
          <input
            placeholder="Slug (kebab-case, e.g. pilates)"
            className="fl-input"
            value={newCat.slug}
            onChange={(e) => setNewCat({ ...newCat, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
            data-testid="admin-new-category-slug"
          />
          <input
            placeholder="Display name"
            className="fl-input"
            value={newCat.name}
            onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
            data-testid="admin-new-category-name"
          />
          <input
            placeholder="Short description (optional)"
            className="fl-input md:col-span-2"
            value={newCat.description}
            onChange={(e) => setNewCat({ ...newCat, description: e.target.value })}
          />
          <div className="md:col-span-4 flex gap-2 justify-end">
            <button onClick={() => setShowNewCat(false)} className="fl-btn-ghost text-xs"><X size={12} /> Cancel</button>
            <button onClick={createCategory} className="fl-btn text-xs" data-testid="admin-new-category-save">
              <Check size={12} /> Create category
            </button>
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-2" data-testid="admin-category-chips">
        {categories.map((c) => (
          <div
            key={c.slug}
            draggable={renamingSlug !== c.slug}
            onDragStart={() => setDragSlug(c.slug)}
            onDragOver={(e) => { e.preventDefault(); handleDragOver(c.slug); }}
            onDragEnd={handleDragEnd}
            className={`fl-card px-3 py-1.5 flex items-center gap-2 text-xs cursor-grab active:cursor-grabbing transition-opacity ${dragSlug === c.slug ? "opacity-40" : ""}`}
            data-testid={`admin-cat-chip-${c.slug}`}
          >
            <GripVertical size={11} className="text-zinc-500" />
            {renamingSlug === c.slug ? (
              <>
                <input
                  autoFocus
                  className="fl-input h-6 py-0 text-xs"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && renameCategory(c.slug)}
                />
                <button onClick={() => renameCategory(c.slug)} className="text-[#f47a20]" title="Save"><Check size={12} /></button>
                <button onClick={() => setRenamingSlug(null)} className="text-zinc-500" title="Cancel"><X size={12} /></button>
              </>
            ) : (
              <>
                <CategoryIcon name={c.icon} size={13} className="text-[#f47a20]" />
                <span className="text-zinc-100 font-medium">{c.name}</span>
                <span className="text-zinc-500 text-[10px]">·</span>
                <span className="text-zinc-500 text-[10px]">{grouped[c.slug]?.length || 0}</span>
                <select
                  value={c.icon || ""}
                  onChange={(e) => setCategoryIcon(c, e.target.value)}
                  className="bg-transparent border-none text-[10px] text-zinc-500 hover:text-white cursor-pointer focus:outline-none"
                  title="Icon"
                  data-testid={`admin-cat-icon-${c.slug}`}
                >
                  <option value="">—</option>
                  {CATEGORY_ICON_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => { setRenamingSlug(c.slug); setRenameValue(c.name); }}
                  className="text-zinc-500 hover:text-white ml-1"
                  title="Rename"
                  data-testid={`admin-cat-rename-${c.slug}`}
                ><Pencil size={11} /></button>
                <button
                  onClick={() => deleteCategory(c.slug)}
                  className="text-zinc-500 hover:text-red-400"
                  title="Delete"
                  data-testid={`admin-cat-delete-${c.slug}`}
                ><Trash2 size={11} /></button>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="text-[10px] text-zinc-500 mb-4 -mt-3">
        Drag the chip handles to reorder <b>categories</b>. Drag the grip icon on any row to reorder <b>services within a category</b>. Both changes save instantly and update the public <b>/prices</b> page.
      </div>

      {categories.map((c) => (
        <div key={c.slug} className="mb-8" data-testid={`admin-cat-block-${c.slug}`}>
          <div className="flex items-baseline justify-between mb-2">
            <div className="flex items-center gap-2">
              <CategoryIcon name={c.icon} size={18} className="text-[#f47a20]" />
              <div>
                <div className="fl-heading text-xl">{c.name}</div>
                {c.description && <div className="text-xs text-zinc-500">{c.description}</div>}
              </div>
            </div>
            <button
              onClick={() => { setAddingTo(c.slug); setNewSvc({ ...EMPTY_SVC, category: c.slug }); }}
              className="fl-btn-ghost text-xs"
              data-testid={`admin-add-service-${c.slug}`}
            >
              <Plus size={11} /> Add service
            </button>
          </div>

          <div className="fl-card overflow-hidden">
            <div className="grid grid-cols-12 gap-3 p-3 border-b border-white/10 fl-label bg-white/[0.03]">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Description</div>
              <div className="col-span-1 text-center">Duration<br/>(min)</div>
              <div className="col-span-1 text-right">Price (£)</div>
              <div className="col-span-2 text-center">Included in<br/>membership</div>
              <div className="col-span-1 text-center">Active</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>

            {addingTo === c.slug && (
              <div className="grid grid-cols-12 gap-3 p-3 items-center border-b border-white/10 bg-[#f47a20]/[0.05]" data-testid={`admin-add-form-${c.slug}`}>
                <input placeholder="Service name" className="fl-input col-span-3" value={newSvc.name}
                       onChange={(e) => setNewSvc({ ...newSvc, name: e.target.value })} autoFocus data-testid={`admin-add-name-${c.slug}`} />
                <input placeholder="Description" className="fl-input col-span-3" value={newSvc.description}
                       onChange={(e) => setNewSvc({ ...newSvc, description: e.target.value })} />
                <input type="number" className="fl-input col-span-1 text-center" value={newSvc.duration_min}
                       onChange={(e) => setNewSvc({ ...newSvc, duration_min: e.target.value })} />
                <input type="number" step="0.01" className="fl-input col-span-1 text-right" value={newSvc.price}
                       onChange={(e) => setNewSvc({ ...newSvc, price: e.target.value })} data-testid={`admin-add-price-${c.slug}`} />
                <div className="col-span-2 flex gap-1 justify-center">
                  {MEMBERSHIP_KEYS.map((mk) => {
                    const on = (newSvc.included_in || []).includes(mk.id);
                    return (
                      <button key={mk.id}
                        onClick={() => setNewSvc({ ...newSvc, included_in: on ? newSvc.included_in.filter((x) => x !== mk.id) : [...(newSvc.included_in || []), mk.id] })}
                        title={mk.full}
                        className={`text-[10px] w-6 h-6 rounded-full border transition ${on ? "text-black" : "text-zinc-500 border-white/10 hover:border-white/40"}`}
                        style={on ? { background: mk.color, borderColor: mk.color } : undefined}
                      >{mk.label}</button>
                    );
                  })}
                </div>
                <label className="col-span-1 text-xs text-zinc-400 flex items-center justify-center gap-1">
                  <input type="checkbox" checked={newSvc.active} onChange={(e) => setNewSvc({ ...newSvc, active: e.target.checked })} />
                </label>
                <div className="col-span-1 flex gap-1 justify-end">
                  <button onClick={() => addService(c.slug)} className="fl-btn text-xs" data-testid={`admin-add-save-${c.slug}`}><Check size={11} /></button>
                  <button onClick={() => setAddingTo(null)} className="fl-btn-ghost text-xs"><X size={11} /></button>
                </div>
              </div>
            )}

            {(grouped[c.slug] || []).length === 0 && addingTo !== c.slug && (
              <div className="p-6 text-center text-xs text-zinc-500">No services in this category yet.</div>
            )}
            {(grouped[c.slug] || []).map((s) => {
              const isDirty = !!dirty[s.id];
              const isDragging = dragRow?.rowId === s.id;
              return (
                <div key={s.id}
                     draggable
                     onDragStart={(e) => { setDragRow({ rowId: s.id, slug: c.slug }); e.dataTransfer.effectAllowed = "move"; }}
                     onDragOver={(e) => { e.preventDefault(); handleRowDragOver(s.id, c.slug); }}
                     onDragEnd={handleRowDragEnd}
                     className={`grid grid-cols-12 gap-3 p-3 items-center border-b last:border-0 border-white/5 text-sm transition-opacity ${isDirty ? "bg-[#f47a20]/[0.06]" : ""} ${isDragging ? "opacity-40" : ""}`}
                     data-testid={`admin-svc-row-${s.id}`}>
                  <div className="col-span-3 flex items-center gap-2">
                    <span className="text-zinc-500 hover:text-[#f47a20] cursor-grab active:cursor-grabbing shrink-0"
                          title="Drag to reorder"
                          data-testid={`admin-svc-drag-${s.id}`}>
                      <GripVertical size={14} />
                    </span>
                    <input className="fl-input flex-1 min-w-0" value={s.name}
                           onChange={(e) => patchRow(s.id, { name: e.target.value })}
                           data-testid={`admin-svc-name-${s.id}`} />
                  </div>
                  <input className="fl-input col-span-3" value={s.description || ""}
                         onChange={(e) => patchRow(s.id, { description: e.target.value })}
                         placeholder="—" />
                  <input type="number" className="fl-input col-span-1 text-center" value={s.duration_min}
                         onChange={(e) => patchRow(s.id, { duration_min: e.target.value })} />
                  <input type="number" step="0.01" className="fl-input col-span-1 text-right" value={s.price}
                         onChange={(e) => patchRow(s.id, { price: e.target.value })}
                         data-testid={`admin-svc-price-${s.id}`} />
                  <div className="col-span-2 flex gap-1 justify-center">
                    {MEMBERSHIP_KEYS.map((mk) => {
                      const on = (s.included_in || []).includes(mk.id);
                      return (
                        <button key={mk.id}
                          onClick={() => toggleMembership(s, mk.id)}
                          title={`${on ? "Included in" : "Not in"} ${mk.full}`}
                          className={`text-[10px] w-6 h-6 rounded-full border transition ${on ? "text-black font-semibold" : "text-zinc-500 border-white/10 hover:border-white/40"}`}
                          style={on ? { background: mk.color, borderColor: mk.color } : undefined}
                          data-testid={`admin-svc-mem-${s.id}-${mk.id}`}
                        >{mk.label}</button>
                      );
                    })}
                  </div>
                  <label className="col-span-1 flex items-center justify-center">
                    <input type="checkbox" checked={s.active}
                           onChange={(e) => patchRow(s.id, { active: e.target.checked })} />
                  </label>
                  <div className="col-span-1 flex gap-1 justify-end">
                    <button
                      onClick={() => saveRow(s)}
                      disabled={!isDirty}
                      className={`text-xs px-2 py-1 rounded transition ${isDirty ? "bg-[#f47a20] text-black hover:brightness-110" : "text-zinc-600 border border-white/5"}`}
                      title={isDirty ? "Save changes" : "No changes"}
                      data-testid={`admin-svc-save-${s.id}`}
                    ><Save size={11} /></button>
                    <button onClick={() => deleteRow(s)} className="text-zinc-500 hover:text-red-400 px-1"
                            data-testid={`admin-svc-delete-${s.id}`}><Trash2 size={12} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {grouped.__other && grouped.__other.length > 0 && (
        <div className="mb-8">
          <div className="fl-heading text-xl mb-2 text-amber-400">Uncategorised</div>
          <div className="fl-card p-3 text-xs text-zinc-400">
            {grouped.__other.length} service(s) are not attached to a category. Rename their category slug on the row (or create a matching category above).
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
